import { Component, Fragment, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Card, Button } from '@/shared/components/ui'

interface ErrorBoundaryProps {
  children: ReactNode
  /** Shown in the fallback card header, e.g. the tile or page name */
  label?: string
  /** Override the default fallback entirely */
  fallback?: ReactNode
  /**
   * When this value changes, a caught error is cleared. Pass the route path so
   * navigating from a broken /notes/1 to /notes/2 doesn't strand the fallback —
   * React reuses the element instance when only params change, so without this
   * the error is sticky. React Router's own boundary clears on navigation, so
   * omitting it would make this strictly worse than the default.
   */
  resetKey?: string
}

interface ErrorBoundaryState {
  error: Error | null
  /** Bumped by Retry to remount the subtree rather than merely re-render it. */
  attempt: number
}

/**
 * A failed dynamic import leaves a rejected promise cached in the module
 * registry, so remounting re-throws the same error forever. Only a fresh
 * document recovers those — which is the dominant real failure right after a
 * deploy, when the old chunk hashes are gone.
 */
const CHUNK_LOAD_PATTERN =
  /dynamically imported module|Loading chunk|Importing a module script failed/i

/**
 * Catches render errors in its subtree so one crashing tile or page
 * can't blank the whole dashboard. Renders a compact fallback card
 * with the error message and a retry affordance.
 *
 * Usage:
 *   <ErrorBoundary label="Tile"><SomeTile /></ErrorBoundary>
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, attempt: 0 }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null })
    }
  }

  handleRetry = () => {
    const { error } = this.state
    if (error && CHUNK_LOAD_PATTERN.test(error.message ?? '')) {
      window.location.reload()
      return
    }
    this.setState(prev => ({ error: null, attempt: prev.attempt + 1 }))
  }

  render() {
    const { error, attempt } = this.state
    const { children, label = 'Something went wrong', fallback } = this.props

    if (error) {
      if (fallback) return <>{fallback}</>
      return (
        <Card title={label} icon={AlertTriangle} className="items-start justify-center">
          <p className="text-sm text-red-400 mb-3 break-words">
            {error.message || 'An unexpected error occurred'}
          </p>
          <Button variant="secondary" size="sm" onClick={this.handleRetry}>
            Retry
          </Button>
        </Card>
      )
    }

    return <Fragment key={attempt}>{children}</Fragment>
  }
}
